import React from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatMetric } from './analysis.shared';
import { nutrientQueryParamFromRow } from './nutrientReportFoodContributions';

export type RowTone = 'met' | 'borderline' | 'deficient' | 'excess' | 'toxic' | 'na';
export type RowFieldValue = string | number | null | undefined;

export type NutritionTableColumnType = 'text' | 'progress' | 'status' | 'expand';
export type NutritionTableColumn = {
  key: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  type?: NutritionTableColumnType;
};

export type SuggestedRdaColumnType = NutritionTableColumnType;
export type SuggestedRdaColumn = NutritionTableColumn;

export type NutritionReportRow = {
  id: string;
  label: string;
  intake?: string;
  target?: string;
  limits?: string;
  unit?: string;
  recommended?: string;
  currentPlan?: string;
  fields?: Record<string, RowFieldValue>;
  progress: number | null;
  tone: RowTone;
  /** Query value for GET .../nutritional-analysis/foods?nutrient= */
  nutrientApiKey?: string;
  /** Backend-provided explanation of how this row's target was selected/calculated. */
  targetCalculationExplanation?: string;
  /** Explanation of the clinical floor, ceiling, or range shown in Limits. */
  limitCalculationExplanation?: string;
};

export type NutritionSection = {
  id: string;
  title: string;
  accent?: string;
  headerBg?: string;
  rows: NutritionReportRow[];
  columns?: NutritionTableColumn[];
};

export type ToneStyles = Record<RowTone, { label: string; color: string; bg: string; bar: string }>;
export type NutritionSectionTableMode = 'nutrition-report' | 'suggested-rda';

interface NutritionReportSectionTableProps {
  section: NutritionSection;
  toneStyles: ToneStyles;
  mode?: NutritionSectionTableMode;
  hideExpandColumn?: boolean;
  /** When set, each row (Nutrient Report) is clickable to open the food contribution panel. */
  onReportRowClick?: (args: { section: NutritionSection; row: NutritionReportRow }) => void;
  /** Normalized nutrient API keys returned on nutritional-analysis (`hiddenNutrients`). */
  hiddenNutrientKeys?: ReadonlySet<string>;
  /** While this key matches a row's nutrient, its Hide/Unhide control is disabled. */
  nutrientVisibilityBusyKey?: string | null;
  /** Persist hide/unhide via meal plan APIs; not used for Energy. */
  onNutrientVisibilityToggle?: (args: { section: NutritionSection; row: NutritionReportRow; hide: boolean }) => void;
  headerAction?: React.ReactNode;
  expandedRowId?: string | null;
  renderExpandedRow?: (args: { section: NutritionSection; row: NutritionReportRow }) => React.ReactNode;
}

const headerCellSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 700,
  color: '#98A2B3',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottomColor: '#E5E7EB',
  whiteSpace: 'nowrap',
} as const;

function renderHeaderLabel(label: string): React.ReactNode {
  const unitSuffixMatch = label.match(/^(.*)\s+\(([^)]+)\)$/);
  if (!unitSuffixMatch) return label;

  return (
    <>
      {unitSuffixMatch[1]}{' '}
      <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0 }}>
        ({unitSuffixMatch[2]})
      </Box>
    </>
  );
}

const defaultSuggestedRdaColumns: NutritionTableColumn[] = [
  { key: 'unit', label: 'Unit', minWidth: 82, align: 'center' },
  { key: 'recommended', label: 'Recommended RDA', minWidth: 140, align: 'center' },
  { key: 'currentPlan', label: 'Current Plan', minWidth: 120, align: 'center' },
  { key: 'progress', label: '% Met', minWidth: 184, align: 'left', type: 'progress' },
  { key: 'status', label: 'Status', minWidth: 126, align: 'center', type: 'status' },
];

const defaultReportColumns: NutritionTableColumn[] = [
  { key: 'intake', label: 'Intake', minWidth: 96, align: 'center' },
  { key: 'target', label: 'Target', minWidth: 96, align: 'center' },
  { key: 'limits', label: 'Limits', minWidth: 96, align: 'center' },
  { key: 'progress', label: 'Progress', minWidth: 170, align: 'left', type: 'progress' },
  { key: 'status', label: 'Status', minWidth: 96, align: 'center', type: 'status' },
  { key: 'expand', label: '', minWidth: 88, align: 'left', type: 'expand' },
];

const pdfColumnWidthByKey: Record<string, string> = {
  nutrient: '22%',
  intake: '14%',
  target: '14%',
  limits: '16%',
  progress: '20%',
  status: '14%',
};

const pdfSectionHeaderHeight = 38;
const pdfTableHeaderHeight = 36;
const pdfTableBodyHeight = 38;
const pdfHeaderPadding = `0 16px`;
const pdfBodyPadding = `0px 16px 12px 16px`;
const pdfInlineTextSx = {
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
};

function getColumnType(column: NutritionTableColumn): NutritionTableColumnType {
  if (column.type) return column.type;
  if (column.key === 'progress') return 'progress';
  if (column.key === 'status') return 'status';
  if (column.key === 'expand') return 'expand';
  return 'text';
}

function getSuggestedTextValue(row: NutritionReportRow, key: string): RowFieldValue {
  if (key === 'unit') return row.unit;
  if (key === 'recommended') return row.recommended;
  if (key === 'currentPlan') return row.currentPlan;
  return row.fields?.[key];
}

function getReportTextValue(row: NutritionReportRow, key: string): RowFieldValue {
  if (key === 'intake') return row.intake;
  if (key === 'target') return row.target;
  if (key === 'limits') return row.limits;
  return row.fields?.[key];
}

function toDisplayValue(value: RowFieldValue): string {
  if (value == null || value === '') return '--';
  return String(value);
}

function isEnergyReportRow(row: NutritionReportRow): boolean {
  return row.nutrientApiKey === 'energy' || row.id === 'energy-row';
}

export const NutritionReportSectionTable: React.FC<NutritionReportSectionTableProps> = ({
  section,
  toneStyles,
  mode = 'nutrition-report',
  hideExpandColumn = false,
  onReportRowClick,
  hiddenNutrientKeys,
  nutrientVisibilityBusyKey = null,
  onNutrientVisibilityToggle,
  headerAction,
  expandedRowId = null,
  renderExpandedRow,
}) => {
  const isSuggestedRda = mode === 'suggested-rda';
  const handleRowClick = onReportRowClick
    ? (row: NutritionReportRow) => (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button,a[href]')) return;
        e.preventDefault();
        onReportRowClick({ section, row });
      }
    : null;
  const accent = section.accent || '#0B89B7';
  const headerBg = section.headerBg || '#EAF8FD';
  const tableColumns = section.columns?.length
    ? section.columns
    : isSuggestedRda
      ? defaultSuggestedRdaColumns
      : defaultReportColumns;

  return (
      <Box sx={{ border: hideExpandColumn ? '1px solid #E4E7EC' : '1px solid #E4E7EC', borderRadius: isSuggestedRda || hideExpandColumn ? '8px' : '12px', overflow: 'hidden', bgcolor: '#fff' }}>
        {isSuggestedRda || hideExpandColumn ? (
          <Box
            sx={{
              px: 2.2,
              py: hideExpandColumn ? 0 : 0.75,
              minHeight: hideExpandColumn ? `${pdfSectionHeaderHeight}px` : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderBottom: '1px solid #EAECF0',
              bgcolor: hideExpandColumn ? headerBg : 'transparent',
              borderRadius: hideExpandColumn ? '0' : '0',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '0.95rem', md: hideExpandColumn ? '0.85rem' : '1.02rem' },
                fontWeight: 700,
                color: hideExpandColumn ? accent : '#1D2939',
                lineHeight: hideExpandColumn ? 1 : undefined,
                textTransform: hideExpandColumn ? 'uppercase' : 'none',
                letterSpacing: hideExpandColumn ? '0.05em' : 'normal',
              }}
            >
              {section.title}
            </Typography>
            {headerAction ? <Box sx={{ flexShrink: 0 }}>{headerAction}</Box> : null}
          </Box>
        ) : (
        <Box
          sx={{
            px: 1.25,
            py: 0.75,
            bgcolor: headerBg,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.65,
            minWidth: '100%',
          }}
        >
          <Box sx={{ width: 3, height: 14, borderRadius: 99, bgcolor: accent }} />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: accent,
            }}
          >
            {section.title}
          </Typography>
        </Box>
      )}

      <Table
        size="small"
        aria-label={`${section.title} nutrient table`}
        sx={hideExpandColumn || isSuggestedRda ? { tableLayout: 'fixed', width: '100%' } : undefined}
      >
        <TableHead>
          <TableRow
            sx={{
              '& .MuiTableCell-head': {
                bgcolor: hideExpandColumn ? '#fff' : '#F8FAFC',
                borderBottomColor: '#E5E7EB',
              },
            }}
          >
            <TableCell
              sx={{
                ...headerCellSx,
                width: { xs: '32%', md: isSuggestedRda ? '21%' : '28%' },
                ...(hideExpandColumn ? { width: pdfColumnWidthByKey.nutrient } : {}),
                p: hideExpandColumn ? pdfHeaderPadding : undefined,
                ...(hideExpandColumn ? { lineHeight: 1, verticalAlign: 'middle', height: pdfTableHeaderHeight } : {}),
              }}
            >
              Nutrient
            </TableCell>

            {tableColumns.map((column) => {
              const columnType = getColumnType(column);
              const align = column.align ?? (columnType === 'progress' ? 'left' : 'center');

              if (columnType === 'expand') {
                if (hideExpandColumn) return null;
                return (
                  <TableCell
                    key={`${section.id}-${column.key}`}
                    sx={{
                      width: column.minWidth ?? 26,
                      minWidth: column.minWidth ?? 26,
                      borderBottomColor: '#E5E7EB',
                      bgcolor: '#F8FAFC',
                      p: 0,
                    }}
                  />
                );
              }

              return (
                <TableCell
                  key={`${section.id}-${column.key}`}
                  align={align}
                  sx={{
                    ...headerCellSx,
                    minWidth: column.minWidth ?? (columnType === 'progress' ? 170 : 96),
                    ...(hideExpandColumn && pdfColumnWidthByKey[column.key] ? { width: pdfColumnWidthByKey[column.key] } : {}),
                    textAlign: align,
                    ...(hideExpandColumn && columnType === 'progress' ? { minWidth: 100, width: 100 } : {}),
                    p: hideExpandColumn ? pdfHeaderPadding : undefined,
                    ...(hideExpandColumn ? { lineHeight: 1, verticalAlign: 'middle', height: pdfTableHeaderHeight } : {}),
                  }}
                >
                  {renderHeaderLabel(column.label)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {section.rows.map((row) => {
            const tone = toneStyles[row.tone];
            /** Match Progress column % text: bar in PDF + Suggested RDA, else label color. */
            const progressPercentColor = hideExpandColumn
              ? tone.bar
              : isSuggestedRda
                ? tone.bar
                : tone.color;
            const progressBarWidth = row.progress == null ? 0 : Math.min(100, Math.max(0, row.progress));
            const isExpanded = expandedRowId === row.id;
            const intakeNumber = Number.parseFloat(String(row.intake ?? ''));
            const isExpandableRow = Boolean(onReportRowClick) && Number.isFinite(intakeNumber) && intakeNumber > 0;
            const nutrientKey = nutrientQueryParamFromRow(row);
            const isNutrientHidden = Boolean(!hideExpandColumn && nutrientKey && hiddenNutrientKeys?.has(nutrientKey));

            return (
              <React.Fragment key={row.id}>
                <TableRow
                  onClick={handleRowClick && isExpandableRow ? handleRowClick(row) : undefined}
                  role={handleRowClick && isExpandableRow ? 'button' : undefined}
                  tabIndex={handleRowClick && isExpandableRow ? 0 : undefined}
                  onKeyDown={
                    onReportRowClick && isExpandableRow
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onReportRowClick({ section, row });
                          }
                        }
                      : undefined
                  }
                  sx={{
                    ...(handleRowClick && isExpandableRow
                      ? { cursor: 'pointer', '&:hover': { bgcolor: isNutrientHidden ? '#F2F4F7' : 'rgba(2, 132, 199, 0.04)' } }
                      : handleRowClick
                        ? { cursor: 'not-allowed' }
                      : hideExpandColumn
                        ? {
                            '& .MuiTableCell-root': {
                              minHeight: pdfTableBodyHeight,
                              p: pdfBodyPadding,
                              verticalAlign: 'middle',
                            },
                          }
                        : undefined),
                    ...(isNutrientHidden
                      ? {
                          bgcolor: '#F8FAFC',
                          '& .MuiTableCell-root': { color: '#98A2B3' },
                        }
                      : {}),
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: isSuggestedRda ? '0.95rem' : '0.88rem',
                      fontWeight: 600,
                      color:
                        isSuggestedRda && row.tone !== 'na'
                          ? '#1D2939'
                          : row.tone === 'deficient' || row.tone === 'excess' || row.tone === 'toxic'
                            ? '#D92D20'
                            : '#1D2939',
                      borderBottomColor: '#E5E7EB',
                      whiteSpace: hideExpandColumn ? 'normal' : 'nowrap',
                      ...(hideExpandColumn ? { width: pdfColumnWidthByKey.nutrient } : {}),
                      p: hideExpandColumn ? pdfBodyPadding : undefined,
                      ...(hideExpandColumn ? { verticalAlign: 'middle', } : {}),
                    }}
                  >
                    {hideExpandColumn ? (
                      <Box
                        component="span"
                        sx={{ display: 'block', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.4 }}
                      >
                        {row.label}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {row.label}
                        {row.targetCalculationExplanation ? (
                          <Tooltip
                            title={row.targetCalculationExplanation}
                            placement="top"
                            arrow
                            enterDelay={150}
                          >
                            <Box
                              component="span"
                              aria-label={`${row.label} target calculation details`}
                              sx={{ display: 'inline-flex', color: '#20AEDC', cursor: 'help', flexShrink: 0 }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            </Box>
                          </Tooltip>
                        ) : null}
                      </Box>
                    )}
                  </TableCell>

                  {tableColumns.map((column) => {
                    const columnType = getColumnType(column);

                    if (columnType === 'expand') {
                      if (hideExpandColumn) return null;
                      const showVisibilityToggle =
                        !hideExpandColumn
                        && Boolean(onNutrientVisibilityToggle)
                        && Boolean(nutrientKey)
                        && !isEnergyReportRow(row);
                      const busy = Boolean(nutrientKey && nutrientVisibilityBusyKey === nutrientKey);
                      return (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align="left"
                          sx={{
                            borderBottomColor: '#E5E7EB',
                            width: column.minWidth ?? 26,
                            minWidth: column.minWidth ?? 26,
                            pl: 0.15,
                            pr: 0,
                            py: 0,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.15 }}
                          >
                            {showVisibilityToggle ? (
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  isNutrientHidden
                                    ? 'Unhide for this meal plan. This nutrient will appear again in the nutrient-report PDF export.'
                                    : 'Hide for this meal plan. Hidden nutrients are excluded from the nutrient-report PDF export.'
                                }
                              >
                                <span>
                                  <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    disabled={busy}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onNutrientVisibilityToggle?.({ section, row, hide: !isNutrientHidden });
                                    }}
                                    sx={{
                                      minWidth: 0,
                                      px: 0.4,
                                      py: 0,
                                      fontFamily: 'Inter, sans-serif',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      color: '#667085',
                                      textTransform: 'none',
                                      lineHeight: 1.2,
                                      '&:hover': { bgcolor: 'rgba(102, 112, 133, 0.08)' },
                                    }}
                                  >
                                    {isNutrientHidden ? 'Unhide' : 'Hide'}
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : null}
                            <Tooltip title={isExpandableRow ? '' : 'No intake to expand'} arrow placement="top">
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <KeyboardArrowDownRoundedIcon
                                  sx={{
                                    fontSize: 16,
                                    color: '#98A2B3',
                                    opacity: isExpandableRow ? 1 : 0.45,
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 180ms ease',
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      );
                    }

                  if (columnType === 'progress') {
                    if (hideExpandColumn) {
                      return (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align="center"
                          sx={{
                            borderBottomColor: '#E5E7EB',
                            p: pdfBodyPadding,
                            width: pdfColumnWidthByKey.progress,
                            verticalAlign: 'middle',
                          }}
                        >
                          {row.progress == null ? (
                            <Typography
                              component="span"
                              sx={{
                                ...pdfInlineTextSx,
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.88rem',
                                fontWeight: 600,
                                color: '#475467',
                             
                              }}
                            >
                              --
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '0.88rem',
                                  lineHeight: 1.25,
                                  minHeight: '1.25em',
                                  marginTop: '12px',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: 6,
                                    borderRadius: 999,
                                    bgcolor: '#EAECF0',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${progressBarWidth}%`,
                                      height: '100%',
                                      borderRadius: 999,
                                      bgcolor: tone.bar,
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={`${row.id}-${column.key}`} sx={{ borderBottomColor: '#E5E7EB', py: 1.2 }}>
                        {row.progress == null ? (
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>--</Typography>
                        ) : (
                          <Box
                            sx={{
                              display: 'block',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                height: isSuggestedRda ? 8 : 6,
                                borderRadius: 999,
                                bgcolor: '#EAECF0',
                                overflow: 'hidden',
                              }}
                            >
                              <Box sx={{ width: `${progressBarWidth}%`, height: '100%', borderRadius: 999, bgcolor: tone.bar }} />
                            </Box>
                          </Box>
                        )}
                      </TableCell>
                    );
                  }

                  if (columnType === 'status') {
                    return (
                      <TableCell
                        key={`${row.id}-${column.key}`}
                        align="center"
                        sx={{
                          borderBottomColor: '#E5E7EB',
                          ...(hideExpandColumn ? { width: pdfColumnWidthByKey.status } : {}),
                          p: hideExpandColumn ? pdfBodyPadding : undefined,
                          ...(hideExpandColumn ? { verticalAlign: 'middle' } : {}),
                        }}
                      >
                        {isSuggestedRda ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 1.2,
                              py: 0.42,
                              borderRadius: 999,
                              border: '1px solid #E4E7EC',
                              bgcolor: '#fff',
                            }}
                          >
                            <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, display: 'block', marginRight: '6px' }}>
                              <circle cx="3" cy="3" r="3" fill={progressPercentColor} />
                            </svg>
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.76rem',
                                fontWeight: 700,
                                color: progressPercentColor,
                                lineHeight: 1,
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              {tone.label}
                            </Typography>
                          </Box>
                        ) : hideExpandColumn ? (
                          <Box
                            component="span"
                            sx={{
                              width: '100%',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.88rem',
                              fontWeight: 700,
                              color: progressPercentColor,
                            
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: progressPercentColor,
                                fontSize: '0.78rem',
                                lineHeight: 1,
                                display: 'inline-block',
                                mr: '6px',
                              }}
                            >
                              {'\u2022'}
                            </Box>
                            {tone.label}
                          </Box>
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 1,
                              py: 0.35,
                              borderRadius: 999,
                              bgcolor: tone.bg,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: progressPercentColor,
                                lineHeight: 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {tone.label}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    );
                  }

                  const align = column.align ?? 'center';
                  const value = isSuggestedRda
                    ? toDisplayValue(getSuggestedTextValue(row, column.key))
                    : toDisplayValue(getReportTextValue(row, column.key));
                  const isCurrentPlan = column.key === 'currentPlan';
                  const isUnit = column.key === 'unit';
                  const isIntake = column.key === 'intake';
                  const isTarget = column.key === 'target';
                  const isLimits = column.key === 'limits';

                  return (
                    <TableCell
                      key={`${row.id}-${column.key}`}
                      align={align}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: isSuggestedRda ? '0.88rem' : hideExpandColumn ? '0.88rem' : isIntake ? '0.82rem' : '0.8rem',
                        fontWeight: isSuggestedRda
                          ? isCurrentPlan
                            ? 700
                            : isUnit
                              ? 500
                              : 600
                          : hideExpandColumn
                            ? isIntake
                              ? 700
                              : 600
                            : isIntake
                              ? 700
                              : 400,
                        color: isSuggestedRda
                          ? isCurrentPlan
                            ? row.tone === 'na'
                              ? '#475467'
                              : tone.bar
                            : isUnit
                              ? '#98A2B3'
                              : '#475467'
                          : isIntake
                            ? row.tone === 'deficient' || row.tone === 'excess' || row.tone === 'toxic'
                              ? '#D92D20'
                              : '#1D2939'
                            : isTarget
                              ? '#475467'
                              : isLimits
                                ? '#667085'
                                : '#475467',
                        borderBottomColor: '#E5E7EB',
                        whiteSpace: 'nowrap',
                        ...(hideExpandColumn && pdfColumnWidthByKey[column.key] ? { width: pdfColumnWidthByKey[column.key] } : {}),
                        textAlign: align,
                        p: hideExpandColumn ? pdfBodyPadding : undefined,
                        ...(hideExpandColumn ? { verticalAlign: 'middle', } : {}),
                      }}
                    >
                      {hideExpandColumn ? (
                        <Box component="span" sx={pdfInlineTextSx}>
                          {value === '--' ? '-' : value}
                        </Box>
                      ) : isLimits && row.limitCalculationExplanation ? (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                          {value}
                          <Tooltip title={row.limitCalculationExplanation} placement="top" arrow enterDelay={150}>
                            <Box
                              component="span"
                              aria-label={`${row.label} clinical limit details`}
                              sx={{ display: 'inline-flex', color: '#20AEDC', cursor: 'help', flexShrink: 0 }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            </Box>
                          </Tooltip>
                        </Box>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                  })}
                </TableRow>
                {isExpanded && renderExpandedRow ? (
                  <TableRow>
                    <TableCell
                      colSpan={1 + tableColumns.length}
                      sx={{ p: 0, borderBottomColor: '#E5E7EB', bgcolor: '#fff' }}
                    >
                      {renderExpandedRow({ section, row })}
                    </TableCell>
                  </TableRow>
                ) : null}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
